import styled, { css, keyframes } from 'styled-components';

import { Text } from '../../../../components/Text/Text';
import { ANIM, COLORS, FIXED_HEADER, REDUCED_MOTION, SPACE, STATUS_COLORS } from '../../../../theme/tokens';

/* A new notification is announced here, not in a pop-up: the bell rings (it swings from its top, less each time),
   then its red dot fades in while it swells to 2x, and settles back quickly with an ease-in. */
const ring = keyframes`
  0% { transform: rotate(0deg); }
  15% { transform: rotate(18deg); }
  30% { transform: rotate(-16deg); }
  45% { transform: rotate(12deg); }
  60% { transform: rotate(-8deg); }
  75% { transform: rotate(4deg); }
  100% { transform: rotate(0deg); }
`;

const dotPop = keyframes`
  0% { opacity: 0; transform: scale(1); animation-timing-function: ease-out; }
  55% { opacity: 1; transform: scale(2); animation-timing-function: ease-in; }
  100% { opacity: 1; transform: scale(1); }
`;

export const Ringer = styled.span<{ $ringing: boolean }>`
  display: inline-flex;
  transform-origin: 50% 8%;
  ${(p) =>
    p.$ringing &&
    css`
      animation: ${ring} ${ANIM.BELL_RING_MS}ms ease-out both;
    `}

  ${REDUCED_MOTION} {
    animation: none;
  }
`;

/* The top bar's notifications bell: the unread count on it, and the list it opens. Modelled on the
   affiliates portal's bell (same tokens), so the two read alike. */
export const Wrap = styled.div`
  position: relative;
  line-height: 0;
`;

export const Trigger = styled.button`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  width: 36px;
  height: 36px;
  padding: 0;
  border: 0;
  border-radius: 9999px;
  background: transparent;
  color: ${COLORS.icon};
  cursor: pointer;
  transition: background ${ANIM.INTERACT_MS}ms ${ANIM.EASE};

  &:hover,
  &[aria-expanded='true'] {
    background: ${COLORS.surfaceMuted};
  }
  &:focus-visible {
    outline: 2px solid ${COLORS.accent};
    outline-offset: 2px;
  }
`;

export const Count = styled(Text).attrs({ tag: 'span' as const })<{ $popping?: boolean }>`
  position: absolute;
  top: 2px;
  right: 0;
  min-width: 16px;
  height: 16px;
  padding: 0 ${SPACE[4]};
  border-radius: 9999px;
  background: ${STATUS_COLORS.negative};
  color: ${COLORS.onAccent};
  font-size: 10px;
  font-weight: 700;
  line-height: 16px;
  text-align: center;
  transform-origin: center;
  ${(p) =>
    p.$popping &&
    css`
      /* right after the bell's ring; hidden until then */
      animation: ${dotPop} ${ANIM.BELL_DOT_POP_MS}ms ${ANIM.BELL_RING_MS}ms both;
    `}

  ${REDUCED_MOTION} {
    animation: none;
  }
`;

/* Says it to a screen reader, where the bell's motion says nothing. */
export const Announce = styled.span`
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
`;

export const Panel = styled.div`
  position: absolute;
  top: 44px;
  right: 0;
  z-index: 60;
  width: 380px;
  max-height: 440px;
  display: flex;
  flex-direction: column;
  border-radius: 16px;
  border: 1px solid ${COLORS.panelBorder};
  background: ${COLORS.surfaceCard};
  box-shadow: ${COLORS.menuShadow};
  line-height: 1.4;
  overflow: hidden;

  ${FIXED_HEADER} {
    position: fixed;
    top: 72px;
    left: 12px;
    right: 12px;
    width: auto;
  }
`;

export const Head = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${SPACE[12]} ${SPACE[16]};
  border-bottom: 1px solid ${COLORS.hairline};
`;

export const HeadTitle = styled(Text).attrs({ tag: 'span' as const })`
  color: ${COLORS.heading};
  font-size: 14px;
  font-weight: 600;
`;

export const MarkAll = styled.button`
  padding: 0;
  border: 0;
  background: transparent;
  color: ${COLORS.accent};
  font: inherit;
  font-size: 12px;
  cursor: pointer;

  &:hover {
    opacity: 0.82;
  }
`;

export const List = styled.div`
  overflow-y: auto;
`;

/* Rows are uncoloured, apart by a hairline; an unread one has a bar in the brand orange down its left edge (inset,
   so its text lines up with a read one's). */
export const Item = styled.div<{ $unread: boolean }>`
  display: flex;
  flex-direction: column;
  gap: ${SPACE[8]};
  padding: ${SPACE[12]} ${SPACE[16]};
  border-bottom: 1px solid ${COLORS.hairline};
  box-shadow: ${(p) => (p.$unread ? `inset 3px 0 0 ${COLORS.ctaSolid}` : 'none')};

  &:last-child {
    border-bottom: 0;
  }
`;

export const ItemMain = styled.button`
  display: flex;
  flex-direction: column;
  gap: ${SPACE[4]};
  width: 100%;
  padding: 0;
  border: 0;
  background: transparent;
  color: inherit;
  font: inherit;
  text-align: left;
  cursor: pointer;

  &:disabled {
    cursor: default;
  }
`;

export const ItemTitle = styled(Text).attrs({ tag: 'span' as const })`
  color: ${COLORS.heading};
  font-size: 13px;
  font-weight: 600;
`;

export const ItemBody = styled(Text).attrs({ tag: 'span' as const })`
  color: ${COLORS.textMuted};
  font-size: 12px;
  white-space: pre-line;
`;

export const ItemDate = styled.span`
  color: ${COLORS.textFaint};
  font-size: 11px;
`;

export const ItemLink = styled.button`
  align-self: flex-start;
  padding: 0;
  border: 0;
  background: transparent;
  color: ${COLORS.accent};
  font: inherit;
  font-size: 12px;
  cursor: pointer;

  &:hover {
    opacity: 0.82;
  }
`;

export const Empty = styled(Text).attrs({ tag: 'paragraph' as const })`
  margin: 0;
  padding: ${SPACE[24]} ${SPACE[16]};
  color: ${COLORS.textMuted};
  font-size: 13px;
  text-align: center;
`;
